import * as Sentry from '@sentry/react'
import { useMemo } from 'react'
import { ErrorHandlerOverrides, getErrorMessageAndDetail } from 'react-admin'

// Status codes not to report on
// see more here: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
const ignoreHttpCodes = [
  401, // Unauthorized
  403, // Forbidden
  404, // Not Found
  407, // Proxy Authentication Required
]

export const useErrorHandlingOverrides = (): ErrorHandlerOverrides => {
  return useMemo(
    // Add Sentry tracking to some error types
    () => ({
      // using 'replace' will prevent the default behaviour from happening (console and notify)
      replace: {
        long_process: (err, opts) => {
          if (opts?.console !== false) console.error(err)
          reportError(err, opts)
        },
      },
      // using 'after' retains the default behaviour, but adds some more functionality
      after: {
        unexpected: (err, opts) => {
          reportError(err, opts)
        },
        data_provider: (err, opts) => {
          const status = err['status']
          const reportableError = status >= 400 && status < 600 && !ignoreHttpCodes.includes(status)
          if (reportableError) {
            reportError(err, opts)
          }
        },
        expected: (err, opts) => {
          reportExpectedError(err, opts)
        },
      },
    }),
    []
  )
}

const reportExpectedError = (err, opts) => {
  Sentry.withScope((scope) => {
    scope.setLevel(opts?.scopeContext?.level || 'info')
    scope.setTag('expected', 'true')

    const captureContext = opts?.captureContext || opts?.getCaptureContext?.()
    if (err instanceof Error) {
      Sentry.captureException(err, captureContext)
    } else {
      const { message, detail } = getErrorMessageAndDetail(err, opts)
      Sentry.captureException(new Error(message, detail || err), captureContext)
    }
  })
}

const reportError = (err, opts) => {
  const captureContext = opts?.captureContext || opts?.getCaptureContext?.()
  if (err instanceof Error) {
    Sentry.captureException(err, captureContext)
  } else {
    const { message, detail } = getErrorMessageAndDetail(err, opts)
    Sentry.captureException(new Error(message, detail || err), captureContext)
  }
}
